import { Controller, ValidateResult } from 'react-hook-form';
import { FormError } from 'shared/components/FormControl';
import styled from 'styled-components';

import ComputedPropertyInput from 'dashboard/apps/DashboardLayoutEditor/ComputedPropertyInput';
import Accordion from 'dashboard/components/Accordion';
import { CaseLayoutTab } from 'dashboard/models/case-template';
import Button from 'design-system/components/Button';
import Input from 'design-system/components/Input';
import Spacer from 'design-system/components/Spacer';
import { HStack } from 'design-system/components/Stack';
import { Label, Paragraph } from 'design-system/components/Text';
import { JsonSchema } from 'lib/json-schema';
import { useFormSecurely } from 'lib/security/reactHookForm';

const ToggleRowWrapper = styled('div')`
  display: flex;
  width: 100%;
  align-items: center;
  justify-content: space-between;
`;

const ToggleLabelContainer = styled('div')`
  display: flex;
  flex-direction: column;
  align-self: flex-start;
  justify-content: space-between;
`;

interface FormEditCaseLayoutTabProps {
  cancel: () => void;
  submit: (tab: CaseLayoutTab) => void;
  tab: CaseLayoutTab;
  validateTabName: (tabName: string, tabToken?: string) => ValidateResult;
  dataSchema: JsonSchema;
}

const FormEditCaseLayoutTab = ({
  cancel,
  submit,
  tab,
  validateTabName,
  dataSchema,
}: FormEditCaseLayoutTabProps) => {
  const badge = tab['badge'] ?? {
    'return-type': 'string',
    'is-required': false,
    'resolution-mechanism': {
      'resolution-type': 'custom-code',
      'serialized-code': null,
    },
  };

  const {
    register: wrappedRegister,
    handleSubmit,
    control,
    formState: { isValid, errors },
  } = useFormSecurely({ defaultValues: { tabName: tab.name, badge }, mode: 'onChange' });

  return (
    <form
      onSubmit={handleSubmit(({ tabName, badge }) => {
        // massage the return value of the form to be the full updated tab object
        submit({
          ...tab,
          name: tabName,
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          badge,
        });
      })}
    >
      <Label size='md' htmlFor='tabName' weight='bold'>
        Tab name
      </Label>
      <Input
        {...wrappedRegister('tabName', {
          validate: (tabName) => validateTabName(tabName, tab.token),
        })}
        id='tabName'
        title='Edit Tab Name'
        type='text'
        placeholder='e.g. Summary'
      />
      {errors.tabName && <FormError role='alert'>{errors.tabName.message}</FormError>}
      <Spacer height={4} />
      <Accordion title='Advanced Options' defaultExpanded={false}>
        <Spacer height={3} />
        <ToggleRowWrapper>
          <ToggleLabelContainer>
            <Label size='md' weight='bold'>
              Alert Badge
            </Label>
          </ToggleLabelContainer>
        </ToggleRowWrapper>
        <Paragraph>
          If a string is returned, an alert badge will show with the returned string.
        </Paragraph>
        <Spacer height={1} />
        <Controller
          control={control}
          name='badge'
          render={({ field: { onChange, value } }) => (
            <ComputedPropertyInput
              /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
              computedProperty={value}
              dataSchema={dataSchema}
              onComputedPropertyUpdate={onChange}
            />
          )}
        />
      </Accordion>
      <Spacer height={3} />
      <HStack equal={true}>
        <Button
          variant='basic'
          id='confirmation-modal__cancel'
          data-test='confirmation-modal__cancel'
          autoFocus={true}
          onClick={cancel}
        >
          Cancel
        </Button>
        <Button
          type='submit'
          variant='primary'
          id='confirmation-modal__confirm'
          data-test='confirmation-modal__confirm'
          disabled={!isValid}
        >
          Confirm
        </Button>
      </HStack>
    </form>
  );
};

export default FormEditCaseLayoutTab;
